import React, { useEffect, useRef } from "react";
import {
    Checkbox,
    CheckboxGroup,
    Dialog,
    DialogTrigger,
    Disclosure,
    DisclosureGroup,
    DisclosurePanel,
    Heading,
    Modal,
} from "react-aria-components";
import { t } from "ttag";

import type {
    ServerCmsTypesCategoryNavigation as CategoryNavigation,
    ServerCmsTypesFilterFacet as FilterFacet,
} from "@reactivated";

import { Themed } from "../../styles/themes";
import { ButtonPrimary, ButtonSecondary, Clickable } from "../Clickables";
import { Svg } from "../Svg";
import type { SelectedFilters } from "./filterProducts";

import styles from "./FilterControls.module.scss";

interface CategoryNavAndFacetsProps {
    categoryNavigation: CategoryNavigation;
    currentPath: string;
    hasActiveFilters: boolean;
    clearAllFilters: () => void;
    defaultExpandedKeys: string[];
    adjustedFacets: FilterFacet[];
    selectedFilters: SelectedFilters;
    setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>;
    inModal?: boolean;
}

const CategoryNavAndFacets = ({
    categoryNavigation,
    currentPath,
    hasActiveFilters,
    clearAllFilters,
    defaultExpandedKeys,
    adjustedFacets,
    selectedFilters,
    setSelectedFilters,
    inModal,
}: CategoryNavAndFacetsProps) => (
    <>
        <Disclosure
            defaultExpanded
            className={styles.categorySidebarDisclosure}
        >
            <Clickable
                slot="trigger"
                className={styles.categoryDisclosureTrigger}
            >
                {t`Categories`}
                <Svg
                    name="caret-right"
                    className={styles.disclosureCaret}
                    aria-hidden="true"
                />
            </Clickable>
            <DisclosurePanel>
                <div className={styles.categoryHeading}>
                    {categoryNavigation.heading}
                </div>
                <ul className={styles.categoryList}>
                    {categoryNavigation.items.map((item) => (
                        <li key={item.url}>
                            <Clickable
                                href={item.url}
                                aria-current={
                                    item.url === currentPath
                                        ? "page"
                                        : undefined
                                }
                                className={styles.categoryLink}
                            >
                                {item.title}
                            </Clickable>
                        </li>
                    ))}
                </ul>
            </DisclosurePanel>
        </Disclosure>

        <div className={styles.filterByHeader}>
            <span>{t`Filter By`}</span>
            {hasActiveFilters && !inModal && (
                <Clickable
                    onPress={clearAllFilters}
                    className={styles.clearAll}
                >
                    {t`Clear All`}
                </Clickable>
            )}
        </div>

        <DisclosureGroup
            allowsMultipleExpanded
            defaultExpandedKeys={defaultExpandedKeys}
        >
            {adjustedFacets.map((facet) => {
                const hasValues = facet.values.some((v) => v.product_count > 0);
                if (!hasValues) return null;
                return (
                    <Disclosure
                        key={facet.slug}
                        id={facet.slug}
                        className={styles.sidebarDisclosure}
                    >
                        <Clickable
                            slot="trigger"
                            className={styles.disclosureTrigger}
                        >
                            <span>{facet.name}</span>
                            <Svg
                                name="caret-right"
                                className={styles.disclosureCaret}
                                aria-hidden="true"
                            />
                        </Clickable>
                        <DisclosurePanel>
                            <CheckboxGroup
                                aria-label={facet.name}
                                value={selectedFilters[facet.slug] ?? []}
                                onChange={(values) =>
                                    setSelectedFilters((prev) => {
                                        const updated = { ...prev };
                                        if (values.length === 0) {
                                            delete updated[facet.slug];
                                        } else {
                                            updated[facet.slug] = values;
                                        }
                                        return updated;
                                    })
                                }
                            >
                                {facet.values
                                    .filter((value) => value.product_count > 0)
                                    .map((value) => (
                                        <Checkbox
                                            key={value.slug}
                                            value={value.slug}
                                            className={styles.facetCheckbox}
                                        >
                                            <div className={styles.checkboxBox}>
                                                <Svg
                                                    name="check"
                                                    className={styles.check}
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <span>
                                                {value.display_value} (
                                                {value.product_count})
                                            </span>
                                        </Checkbox>
                                    ))}
                            </CheckboxGroup>
                        </DisclosurePanel>
                    </Disclosure>
                );
            })}
        </DisclosureGroup>
    </>
);

export const FilterSidebar = (props: CategoryNavAndFacetsProps) => (
    <aside className={styles.filterSidebar}>
        <CategoryNavAndFacets {...props} />
    </aside>
);

interface MobileFilterModalProps extends CategoryNavAndFacetsProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
}

/**
 * Checkboxes update the grid in real-time under the modal so that
 * 1) it's doing the same thing as desktop and
 * 2) it narrows the filters so that you can't ever select a combination
 *    of checkboxes that produces zero results
 *
 * A snapshot of the active filters is captured when the modal opens
 *
 * Close paths:
 * - **Apply**: just closes; changes are already applied
 * - **X / Escape**: restores the snapshot, then closes
 * - **Clear All**: clears all filters and closes
 *
 * Escape handling lives on `<Themed>` because you can't really hook into
 * whatever happens when one presses the Esc key
 */
export const MobileFilterModal = ({
    isOpen,
    onOpenChange,
    ...filterProps
}: MobileFilterModalProps) => {
    const snapshotRef = useRef<SelectedFilters>({});

    const restoreSnapshot = () => {
        filterProps.setSelectedFilters(snapshotRef.current);
    };

    useEffect(() => {
        if (isOpen) {
            snapshotRef.current = structuredClone(filterProps.selectedFilters);
        }
    }, [isOpen]);

    return (
        <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
            <Modal className={styles.filterModal}>
                <Dialog>
                    {({ close }) => (
                        <Themed
                            theme="offWhite"
                            className={styles.filterModalWrapper}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === "Escape") {
                                    restoreSnapshot();
                                }
                            }}
                        >
                            <div className={styles.filterModalHeader}>
                                <Heading
                                    slot="title"
                                    className={styles.filterModalTitle}
                                >
                                    {t`Filter`}
                                </Heading>
                                <Clickable
                                    onPress={() => {
                                        restoreSnapshot();
                                        close();
                                    }}
                                    className={styles.filterModalClose}
                                >
                                    <Svg
                                        name="x"
                                        className={styles.filterModalCloseIcon}
                                        visuallyHiddenText={t`Close`}
                                    />
                                </Clickable>
                            </div>
                            <div className={styles.filterModalContent}>
                                <CategoryNavAndFacets
                                    {...filterProps}
                                    inModal
                                />
                            </div>
                            <div className={styles.filterModalFooter}>
                                <ButtonPrimary
                                    onPress={() => {
                                        filterProps.clearAllFilters();
                                        close();
                                    }}
                                    className={styles.filterModalAction}
                                >
                                    {t`Clear All`}
                                </ButtonPrimary>
                                <ButtonSecondary
                                    onPress={close}
                                    className={styles.filterModalAction}
                                >
                                    {t`Apply`}
                                </ButtonSecondary>
                            </div>
                        </Themed>
                    )}
                </Dialog>
            </Modal>
        </DialogTrigger>
    );
};
